import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, useApiFetch } from "../../api/client";
import { TopBar } from "../../app/TopBar";
import { PageFooter } from "../../app/PageFooter";
import { useAppUser } from "../../app/AppUserContext";
import type { AppRole } from "../../app/AppUserContext";
import { PATHS } from "../../app/paths";

interface AdminUser {
  appUserId: number;
  displayName: string;
  userPrincipalName: string;
  email: string | null;
  appRole: AppRole;
  isActive: boolean;
  lastSeenUtc: string;
}

const ROLES: AppRole[] = ["Viewer", "Uploader", "Admin"];

export function AdminUsers() {
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const { appUserId } = useAppUser();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    apiFetch("admin/users")
      .then((res) => res.json())
      .then(setUsers)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const changeRole = async (u: AdminUser, appRole: AppRole) => {
    setSavingId(u.appUserId);
    setError(null);
    try {
      const res = await apiFetch(`admin/users/${u.appUserId}`, {
        method: "PATCH",
        body: JSON.stringify({ appRole }),
      });
      const updated: AdminUser = await res.json();
      setUsers((prev) => prev.map((x) => (x.appUserId === updated.appUserId ? updated : x)));
    } catch (err) {
      if (err instanceof ApiError) {
        setError((err.body as { detail?: string })?.detail ?? err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <TopBar title="User Permissions">
        <button className="btn ghost" onClick={() => navigate(PATHS.data)}>
          <span className="material-symbols-sharp">folder_shared</span>Resin8 Data
        </button>
        <button className="btn ghost" onClick={() => navigate(PATHS.intake)}>
          <span className="material-symbols-sharp">upload_file</span>Data Intake Tool
        </button>
      </TopBar>

      <main>
        <section className="lane on">
          <h2>User Permissions</h2>
          <p className="lede">
            Anyone who signs in gets Uploader access by default, so they can upload
            and manage their own data. Set Viewer to restrict someone to read-only,
            or Admin for full access including managing other users' permissions.
          </p>

          {error && (
            <div className="sec"><div className="gate narrow"><span className="msg" style={{ color: "var(--salmon)" }}>{error}</span></div></div>
          )}

          <div className="sec">
            <div className="card" style={{ padding: 0, overflow: "auto", maxWidth: "100%" }}>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Last seen</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={4}>Loading…</td></tr>
                  )}
                  {!loading && users.length === 0 && (
                    <tr><td colSpan={4}>No users yet.</td></tr>
                  )}
                  {users.map((u) => (
                    <tr key={u.appUserId}>
                      <td>
                        {u.displayName}
                        {u.appUserId === appUserId && <span style={{ color: "var(--grey)", fontSize: 11.5 }}> (you)</span>}
                      </td>
                      <td>{u.email ?? u.userPrincipalName}</td>
                      <td>{new Date(u.lastSeenUtc).toLocaleString()}</td>
                      <td>
                        <select
                          className="ctl"
                          style={{ width: "auto" }}
                          value={u.appRole}
                          disabled={savingId === u.appUserId}
                          onChange={(e) => changeRole(u, e.target.value as AppRole)}
                        >
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </main>

      <PageFooter />
    </>
  );
}
